
import { Component, Vue, Prop } from 'vue-property-decorator'
import { GetLeakById, UpdateLeakRepair, EndRepair } from '@/api/leaks'
import { ILeakPoint, ILeakFile, LeakStatus, FileType, IPipeDiameter, IPipeMaterial, IRoadSurface, ILeakPosition, ILeakReason, RepairType } from '@/api/types'
import { ListPipeDiameters, ListPipeMaterials, ListRoadSurfaces, ListLeakPositions, ListLeakReasons } from '@/api/baseData'
import LeakStatusTag from '@/views/leaks/components/LeakStatusTag/index.vue'
import LeakMediaUpload from '@/views/leaks/components/LeakMediaUpload/index.vue'
import { formatDate } from '@/utils/index'
import { goBack } from '@/router'
import { IsMobile } from '@/utils/check'

@Component({
  name: 'LeakRepairEdit',
  components: {
    LeakStatusTag,
    LeakMediaUpload
  }
})
export default class extends Vue {

  private loading = false
  // private saving = false
  private leak: ILeakPoint | null = null
  private isUploading = false
  private uploadProgress = 0

  private isFinishMode = false
  private leakId = -1

  get isMobile() {
    return IsMobile()
  }

  // 维修类型常量
  private repairTypeConst = {
    Other: RepairType.Other,
    BeforeMeter: RepairType.BeforeMeter,
    AfterMeter: RepairType.AfterMeter,
    OutSideNets: RepairType.OutSideNets,
    Empty: RepairType.Empty,
    Internal: RepairType.Internal,
    ChangePipe: RepairType.ChangePipe
  }

  // 编辑表单
  private editForm = {
    repair: {
      repairType: undefined as number | undefined,
      part: '',
      reason: '',
      leakLeakage: 0,
      depth: 0,
      repairTime: '',
      roadInfo: '',
      size: 0,
      material: ''
    }
  }

  // 媒体状态
  private currentMedia = {
    repairImages: [] as string[],
    repairVideos: [] as string[]
  }

  // 选项数据
  private pipeDiameterOptions: IPipeDiameter[] = []
  private pipeMaterialOptions: IPipeMaterial[] = []
  private roadSurfaceOptions: IRoadSurface[] = []
  private leakPositionOptions: ILeakPosition[] = []
  private leakReasonOptions: ILeakReason[] = []

  // 表单验证规则
  private repairRules = {
    repairType: [{ required: true, message: '请选择维修类型', trigger: 'change' }],
    part: [{ required: true, message: '请选择维修部位', trigger: 'change' }],
    reason: [{ required: true, message: '请输入原因', trigger: 'change' }],
    leakLeakage: [{ type: 'number', message: '漏量必须为数字' }],
    depth: [{ type: 'number', message: '埋深必须为数字' }]
  }

  // 获取维修图片
  get repairImages() {
    if (!this.leak || !this.leak.repair || !this.leak.repair.files) return []
    return this.leak.repair.files
      .filter(file => file.fileType === FileType.RepairImage)
      .map(file => file.filePath)
  }

  // 获取维修视频
  get repairVideos() {
    if (!this.leak || !this.leak.repair || !this.leak.repair.files) return []
    return this.leak.repair.files
      .filter(file => file.fileType === FileType.RepairVideo)
      .map(file => file.filePath)
  }

  created() {
    // 获取漏点信息和下拉选项
    this.leakId = Number(this.$route.params.id)
    this.isFinishMode = this.$route.params.mode === 'finish'
    this.fetchLeakDetail()
    this.fetchOptions()
  }

  /**
   * 获取漏点详情
   */
  private async fetchLeakDetail() {
    try {
      this.loading = true
      if (this.$route.params.leak) {
        this.leak = JSON.parse(this.$route.params.leak)
      } else {
        const response = await GetLeakById({ id: this.leakId })
        this.leak = response.data
      }

      // 初始化表单数据
      this.initEditForm()

      // 初始化媒体文件
      this.initMediaFiles()
    } catch (error) {
      console.error('获取漏点详情失败', error)
      this.$message.error('获取漏点详情失败')
    } finally {
      this.loading = false
    }
  }

  /**
   * 获取下拉选项数据
   */
  private async fetchOptions() {
    try {
      // 获取管径选项
      const diameterResponse = await ListPipeDiameters()
      this.pipeDiameterOptions = diameterResponse.data || []

      // 获取管材选项
      const materialResponse = await ListPipeMaterials()
      this.pipeMaterialOptions = materialResponse.data || []

      // 获取路面选项
      const roadResponse = await ListRoadSurfaces()
      this.roadSurfaceOptions = roadResponse.data || []

      // 获取漏水部位选项
      const positionResponse = await ListLeakPositions()
      this.leakPositionOptions = positionResponse.data || []

      // 获取漏水原因选项
      const reasonResponse = await ListLeakReasons()
      this.leakReasonOptions = reasonResponse.data || []
    } catch (error) {
      console.error('获取选项数据失败', error)
    }
  }

  /**
   * 初始化编辑表单
   */
  private initEditForm() {
    if (!this.leak) return

    // 如果有维修信息，使用已有信息初始化表单
    if (this.leak.repair) {
      this.editForm.repair = {
        repairType: this.leak.repair.repairType,
        part: this.leak.repair.part || (this.leakPositionOptions.length > 0 ? this.leakPositionOptions[0].name : ''),
        reason: this.leak.repair.reason || (this.leakReasonOptions.length > 0 ? this.leakReasonOptions[0].desc : ''),
        leakLeakage: this.leak.repair.leakLeakage || 0,
        depth: this.leak.repair.depth || 0,
        repairTime: this.leak.repair.repairTime || formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss', true),
        roadInfo: this.leak.repair.roadInfo || this.leak.detection?.roadInfo || (this.roadSurfaceOptions.length > 0 ? this.roadSurfaceOptions[0].name : ''),
        size: this.leak.repair.size || this.leak.detection?.size || (this.pipeDiameterOptions.length > 0 ? this.pipeDiameterOptions[0].size : 0),
        material: this.leak.repair.material || this.leak.detection?.material || (this.pipeMaterialOptions.length > 0 ? this.pipeMaterialOptions[0].name : '')
      }
    } else {
      // 如果没有维修信息，创建新的维修信息
      this.editForm.repair = {
        repairType: undefined,
        part: this.leakPositionOptions.length > 0 ? this.leakPositionOptions[0].name : '',
        reason: this.leakReasonOptions.length > 0 ? this.leakReasonOptions[0].desc : '',
        leakLeakage: 0,
        depth: 0,
        repairTime: formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss', true),
        roadInfo: this.leak.detection?.roadInfo || (this.roadSurfaceOptions.length > 0 ? this.roadSurfaceOptions[0].name : ''),
        size: this.leak.detection?.size || (this.pipeDiameterOptions.length > 0 ? this.pipeDiameterOptions[0].size : 0),
        material: this.leak.detection?.material || (this.pipeMaterialOptions.length > 0 ? this.pipeMaterialOptions[0].name : '')
      }
    }
  }

  /**
   * 初始化媒体文件
   */
  private initMediaFiles() {
    if (!this.leak) return
    this.currentMedia.repairImages = this.repairImages
    this.currentMedia.repairVideos = this.repairVideos
  }

  /**
   * 处理维修媒体变更
   */
  private handleRepairMediaChange(event: {
    checkImages?: string[]
    checkVideos?: string[]
    repairImages?: string[]
    repairVideos?: string[]
  }) {
    if (!this.leak) return;

    // 适配不同的数据结构
    const repairImages = event.repairImages || event.checkImages || [];
    const repairVideos = event.repairVideos || event.checkVideos || [];

    // 更新当前媒体状态
    this.currentMedia.repairImages = repairImages;
    this.currentMedia.repairVideos = repairVideos;

    // 检查维修图片是否超过最大限制
    if (repairImages.length > 6) {
      this.$message.warning('维修图片最多上传6张');
    }

    // 检查是否有媒体文件，更新界面提示
    // this.updateRepairMediaTip({
    //   checkImages: repairImages,
    //   checkVideos: repairVideos
    // });
  }

  private isRepairMediaComplete(): boolean {
    const media = this.currentMedia
    if (media.repairImages.length === 0) {
      this.$message.warning('请上传至少一张维修图片')
      return false
    }
    if (media.repairVideos.length === 0) {
      this.$message.warning('请上传至少一个维修视频')
      return false
    }
    return true
  }

  /**
   * 更新维修媒体提示
   */
  // private updateRepairMediaTip(media: { checkImages: string[], checkVideos: string[] }) {
  //   // 在这里可以根据媒体数据更新UI提示
  //   const tipEl = document.querySelector('.repair-media-tip .el-alert');
  //   if (!tipEl) return;

  //   // 检查所有媒体类型是否都有至少一项
  //   const hasRepairImages = media && media.checkImages && media.checkImages.length > 0;
  //   const hasRepairVideos = media && media.checkVideos && media.checkVideos.length > 0;

  //   if (hasRepairImages && hasRepairVideos) {
  //     // 所有类型都已上传
  //     tipEl.classList.remove('el-alert--warning');
  //     tipEl.classList.add('el-alert--success');
  //     (tipEl.querySelector('.el-alert__title') as HTMLElement).innerText = '维修媒体文件 (已完成) ✓';

  //     // 更新描述文本
  //     const descEl = tipEl.querySelector('.el-alert__description') as HTMLElement;
  //     if (descEl) {
  //       descEl.innerHTML = `
  //         <div>已上传全部必要媒体文件：</div>
  //         <ul style="margin: 5px 0; padding-left: 20px;">
  //           <li>维修图片：${media.checkImages.length}张 ✓</li>
  //           <li>维修视频：${media.checkVideos.length}个 ✓</li>
  //         </ul>
  //       `;
  //     }
  //   } else {
  //     // 有类型未上传，保持警告状态但更新状态
  //     const descEl = tipEl.querySelector('.el-alert__description') as HTMLElement;
  //     if (descEl) {
  //       descEl.innerHTML = `
  //         <div>请上传媒体文件，以下各类型至少需要一个：</div>
  //         <ul style="margin: 5px 0; padding-left: 20px;">
  //           <li>维修图片：${hasRepairImages ? '已上传' + media.checkImages.length + '张 ✓' : '至少需要1张 ✗'}</li>
  //           <li>维修视频：${hasRepairVideos ? '已上传' + media.checkVideos.length + '个 ✓' : '至少需要1个 ✗'}</li>
  //         </ul>
  //       `;
  //     }
  //   }
  // }

  /**
   * 处理上传状态变更
   */
  private handleUploadStatus(status: { uploading: boolean, progress?: number }) {
    this.isUploading = status.uploading
    if (status.progress !== undefined) {
      this.uploadProgress = status.progress
    }
  }

  /**
   * 取消编辑
   */
  private cancelEdit() {
    goBack()
  }

  /**
   * 提交编辑
   */
  private submitEdit() {
    if (this.isFinishMode) {
      this.submitFinishRepair();
    } else {
      this.submitRepairEdit();
    }
  }

  /**
   * 组装维修信息
   */
  private assembleRepairInfo() {
    const repairMediaUpload = this.$refs.repairMediaUpload as any;
    const currentMedia = repairMediaUpload.getCurrentMedia ? repairMediaUpload.getCurrentMedia() : {
      repairImages: this.currentMedia.repairImages,
      repairVideos: this.currentMedia.repairVideos
    };

    // 构造参数
    let params = {
      id: Number(this.leakId),
      repairType: this.editForm.repair.repairType as number,
      part: this.editForm.repair.part,
      reason: this.editForm.repair.reason,
      leakLeakage: this.editForm.repair.leakLeakage,
      depth: this.editForm.repair.depth,
      roadInfo: this.editForm.repair.roadInfo,
      size: this.editForm.repair.size,
      material: this.editForm.repair.material,
      files: [] as ILeakFile[]
    };

    if (currentMedia.repairImages && currentMedia.repairImages.length > 0) {
      currentMedia.repairImages.forEach((image: string) => {
        params.files.push({
          filePath: image,
          fileType: FileType.RepairImage,
        });
      });
    }

    if (currentMedia.repairVideos && currentMedia.repairVideos.length > 0) {
      currentMedia.repairVideos.forEach((video: string) => {
        params.files.push({
          filePath: video,
          fileType: FileType.RepairVideo,
        });
      });
    }

    return params
  }

  /**
   * 提交维修信息编辑
   */
  private async submitRepairEdit() {
    if (this.isUploading) {
      this.$message.warning('请等待文件上传完成后再提交');
      return;
    }

    // 表单验证
    try {
      await (this.$refs.repairForm as any).validate();
    } catch (error) {
      return false;
    }

    try {
      this.loading = true;

      // 获取上传组件中最新的媒体文件
      const params = this.assembleRepairInfo()
      await UpdateLeakRepair(params);
      this.$message.success('维修信息更新成功');
      goBack()
    } catch (error) {
      console.error('维修信息更新失败', error);
      this.$message.error('维修信息更新失败');
    } finally {
      this.loading = false;
    }
  }

  /**
   * 提交完成维修
   */
  private async submitFinishRepair() {
    if (this.isUploading) {
      this.$message.warning('请等待文件上传完成后再提交');
      return;
    }

    // 表单验证
    try {
      await (this.$refs.repairForm as any).validate();
    } catch (error) {
      return false
    }

    if (!this.isRepairMediaComplete()) {
      return false
    }

    try {
      this.loading = true;

      // 获取上传组件中最新的媒体文件
      const params = this.assembleRepairInfo()
      await EndRepair(params);
      if (this.editForm.repair.repairType === this.repairTypeConst.AfterMeter ||
          this.editForm.repair.repairType === this.repairTypeConst.OutSideNets) {
        this.$message.success('已标记为无需维修');
      } else if (this.editForm.repair.repairType === this.repairTypeConst.Other) {
        this.$message.success('已标记为无法维修');
      } else {
        this.$message.success('完成维修');
      }
      goBack()
    } catch (error) {
      console.error('完成维修失败', error);
      this.$message.error('完成维修失败');
    } finally {
      this.loading = false;
    }
  }
}
